/**
 * Price Summary Card
 * 
 * Displays booking price breakdown with subtotal, tax, and total.
 * Uses the TicketCard primitive for the punch-hole divot effect.
 * Used in payment and confirmation flows.
 * 
 * @module butterUI/components/PriceSummaryCard
 */

import { TicketCard, TicketDivider } from '#patterns';
import { formatFeeRate } from '@butter/library/pricing';
import type { BookingLineItem } from '@butter/library/pricing';

export interface PriceSummaryCardProps {
  /** Subtotal amount (before tax) */
  subtotal: number;
  /** Tax amount */
  tax: number;
  /** Total amount (subtotal + tax) */
  total: number;
  /** The total row's label — "Due at Club" when the club collects it. Defaults to "Total". */
  totalLabel?: string;
  /** Tax rate percentage for display (e.g., 8.5 for 8.5%) */
  taxRatePercent?: number;
  /** Setup fee amount (if applicable) */
  setupFee?: number;
  /** Subscription discount amount (if applicable) */
  subscriptionDiscount?: number;
  /** Subscription name for discount label (e.g., "Gold Individual Membership") */
  subscriptionName?: string;
  /**
   * Full label for the discount row, overriding the derived
   * `"{subscriptionName} Discount"`. A promo discount's label is authored by
   * the server (e.g. `Promo SUMMER10 (10% off)`) and appending "Discount" to
   * it would double-say it.
   *
   * NB: a discount belongs in THIS row, not in `lineItems` — the
   * `showSubtotal` reduce sums every line item it is given, so a discount
   * line there renders a net figure under a "Subtotal" label.
   */
  discountLabel?: string;
  /** Overage charge amount (if applicable) */
  overageCharge?: number;
  /**
   * Card processing-fee surcharge (dollars). When > 0, renders a disclosed "Processing Fee"
   * row after Tax. The fee is added after tax (untaxed); `total` must already include it.
   */
  processingFee?: number;
  /** Percentage component of the processing fee (e.g. 2.9) for the row label. */
  processingFeeRate?: number;
  /** Pre-tax line items. When provided, renders labeled rows instead of a single "Subtotal" row. */
  lineItems?: BookingLineItem[];
  /** When true, renders a "Subtotal" row after the line items. Useful when line items include free entries. */
  showSubtotal?: boolean;
  /**
   * The price is not known yet — every amount renders as an em dash instead of a
   * figure, and the rows that depend on a resolved breakdown (discount, overage,
   * setup and processing fee) are not rendered at all.
   *
   * This exists because a booking price is resolved by the SERVER: the browser
   * cannot apply a per-bay premium or the club's pricing mode, so there is no
   * honest number to show while the quote is in flight. Rendering `$0.00`
   * against unresolved props is what this replaces.
   *
   * The `price-summary-*` test ids stay on the page while pending, so a waiting
   * selector still resolves. Its text is NOT a bare "—": each placeholder also
   * carries an `sr-only` "Calculating", so a waiting assertion must match the
   * arrival of a money value (`/^\$/`) rather than the absence of a dash.
   *
   * A screen reader is told twice over: the placeholders read as "Calculating",
   * and one persistent live region (see `liveRegion` below) announces the total
   * when it arrives.
   */
  pending?: boolean;
  /**
   * The user's account credit against `total`, from the signed-in quote (credits D9).
   * Renders "Account credit −$X" and "Due today $Y" BELOW the total: credit is a tender,
   * not a discount, so the total stays what the purchase costs. Integer cents.
   */
  credit?: { appliedCents: number; dueNowCents: number; feeAdjustmentCents?: number };
  /** Optional CSS class name */
  className?: string;
}

/**
 * Price Summary Card Component
 * 
 * Displays a breakdown of booking costs with subtotal, optional tax, total,
 * and subscription-related discounts/charges.
 * 
 * @example
 * ```tsx
 * // Standard booking
 * <PriceSummaryCard
 *   subtotal={49.50}
 *   tax={4.95}
 *   total={54.45}
 *   taxRatePercent={10.00}
 * />
 * ```
 * 
 * @example
 * ```tsx
 * // Booking with subscription discount
 * <PriceSummaryCard
 *   subtotal={49.50}
 *   subscriptionDiscount={49.50}
 *   subscriptionName="Gold Individual Membership"
 *   tax={0}
 *   total={0}
 * />
 * ```
 * 
 * @example
 * ```tsx
 * // Booking with overage
 * <PriceSummaryCard
 *   subtotal={49.50}
 *   subscriptionDiscount={49.50}
 *   subscriptionName="Gold Individual Membership"
 *   overageCharge={25.00}
 *   tax={2.50}
 *   total={27.50}
 * />
 * ```
 */
export function PriceSummaryCard({
  subtotal,
  tax,
  total,
  totalLabel = 'Total',
  taxRatePercent,
  setupFee,
  subscriptionDiscount,
  subscriptionName,
  discountLabel,
  overageCharge,
  processingFee,
  processingFeeRate,
  lineItems,
  showSubtotal = false,
  pending = false,
  credit,
  className = '',
}: PriceSummaryCardProps) {
  // Check if tax is applied
  const hasTax = tax > 0;
  const hasSetupFee = setupFee !== undefined && setupFee > 0;
  const hasSubscriptionDiscount = subscriptionDiscount !== undefined && subscriptionDiscount > 0;
  const hasOverageCharge = overageCharge !== undefined && overageCharge > 0;
  const hasProcessingFee = processingFee !== undefined && processingFee > 0;
  const hasLineItems = lineItems !== undefined && lineItems.length > 0;
  const hasCredit = credit !== undefined && credit.appliedCents > 0;

  /**
   * The one live region, rendered as the FIRST child of `TicketCard` by BOTH
   * branches so it survives the swap between them.
   *
   * That position is the whole trick. `aria-live` announces mutations *inside a
   * region that persists* — if the node itself unmounts when the price lands,
   * assistive tech generally announces nothing at all, which is what the earlier
   * version of this component did while claiming otherwise. React reconciles by
   * position and type, so an identical `<span>` in slot 0 of the same `TicketCard`
   * is kept across the branch change and only its text mutates. Move it, wrap it,
   * or render it from one branch only, and the announcement silently stops
   * working — with nothing failing to tell you.
   */
  const liveRegion = (
    <span className="sr-only" role="status" aria-live="polite">
      {pending
        ? 'Calculating your price'
        : `${totalLabel} ${total.toFixed(2)} dollars${
            hasCredit ? `, due today ${(credit.dueNowCents / 100).toFixed(2)} dollars` : ''
          }`}
    </span>
  );

  if (pending) {
    // An em dash is announced as nothing at all, so each placeholder pairs an
    // `sr-only` word with an `aria-hidden` glyph. Note this makes the rendered
    // text of the `price-summary-*` test ids "Calculating—", never a bare dash —
    // a waiting assertion has to match the ARRIVAL of a money value, not the
    // absence of a placeholder.
    const placeholder = (
      <>
        <span className="sr-only">Calculating</span>
        <span aria-hidden="true">&mdash;</span>
      </>
    );

    return (
      <TicketCard className={className} borderColor="var(--theme-200)">
        {liveRegion}
        <div className="px-6 pt-6" aria-busy="true">
          <h3 className="text-sm font-normal text-gray-500 mb-4">Price Summary</h3>
          <div className="space-y-3 pb-1">
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">Subtotal</span>
              <span className="font-semibold text-fg-primary" data-testid="price-summary-subtotal">
                {placeholder}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">Tax</span>
              <span className="font-semibold text-fg-primary" data-testid="price-summary-tax">
                {placeholder}
              </span>
            </div>
          </div>
        </div>

        <TicketDivider punch />

        <div className="px-6 pb-6">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-fg-primary">Total</span>
            <span className="font-bold text-lg text-fg-primary" data-testid="price-summary-total">
              {placeholder}
            </span>
          </div>
        </div>
      </TicketCard>
    );
  }

  return (
    <TicketCard className={className} borderColor="var(--theme-200)">
      {liveRegion}
      {/* Top section with padding */}
      <div className="px-6 pt-6">
        <h3 className="text-sm font-normal text-gray-500 mb-4">
          Price Summary
        </h3>
        
        <div className="space-y-3 pb-1">
          {/* Line items (labeled rows) — replaces generic Subtotal row when provided */}
          {hasLineItems ? (
            lineItems.map((item) => (
              <div key={item.key} className="flex justify-between items-start">
                <div>
                  <span
                    className={
                      item.label === 'Overage Charges'
                        ? 'text-blue-600'
                        : item.highlight
                          ? 'text-theme-600'
                          : 'text-fg-primary'
                    }
                  >
                    {item.label}
                  </span>
                  {item.subtitle && (
                    <p className="text-xs text-fg-secondary mt-0.5">{item.subtitle}</p>
                  )}
                </div>
                <span className="font-semibold text-fg-primary">
                  {item.displayAmount ?? `$${item.amountDollars.toFixed(2)}`}
                </span>
              </div>
            ))
          ) : (
            /* Fallback: single Subtotal row (backward compat) */
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">Subtotal</span>
              <span
              className="font-semibold text-fg-primary"
              data-testid="price-summary-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
          )}

          {/* Subtotal row after itemized list — used when line items include free entries */}
          {hasLineItems && showSubtotal && (
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">Subtotal</span>
              <span className="font-semibold text-fg-primary" data-testid="price-summary-subtotal">
                ${lineItems.reduce((s, i) => s + i.amountDollars, 0).toFixed(2)}
              </span>
            </div>
          )}
          
          {/* Setup Fee */}
          {hasSetupFee && (
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">Setup Fee</span>
              <span className="font-semibold text-fg-primary">
                ${setupFee.toFixed(2)}
              </span>
            </div>
          )}
          
          {/* Subscription Discount */}
          {hasSubscriptionDiscount && (
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">
                {discountLabel ?? `${subscriptionName || 'Membership'} Discount`}
              </span>
              {/* testid on the AMOUNT span like its sibling rows — a row-level
                  testid hands scrapers the label text glued to the number. */}
              <span className="font-semibold text-green-600" data-testid="price-summary-discount">
                -${subscriptionDiscount.toFixed(2)}
              </span>
            </div>
          )}
          
          {/* Overage Charge */}
          {hasOverageCharge && (
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">Overage Charge</span>
              <span className="font-semibold text-orange-600">
                ${overageCharge.toFixed(2)}
              </span>
            </div>
          )}
          
          {/* Tax (only show if applied) */}
          {hasTax && (
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">
                Tax {taxRatePercent !== undefined && `(${taxRatePercent.toFixed(2)}%)`}
              </span>
              <span
              className="font-semibold text-fg-primary"
              data-testid="price-summary-tax">
                ${tax.toFixed(2)}
              </span>
            </div>
          )}

          {/* Processing Fee (card surcharge — after tax, only when the club passes it on) */}
          {hasProcessingFee && (
            <div className="flex justify-between items-center">
              <span className="text-fg-primary">
                Processing Fee {processingFeeRate !== undefined && `(${formatFeeRate(processingFeeRate)})`}
              </span>
              <span
              className="font-semibold text-fg-primary"
              data-testid="price-summary-processing-fee">
                ${processingFee.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Ticket Divider with punch holes - full width */}
      <TicketDivider punch />
      
      {/* Total Section */}
      <div className="px-6 pb-6">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-fg-primary">{totalLabel}</span>
          <span 
          className="font-bold text-lg text-fg-primary" 
          data-testid="price-summary-total"
          >
            ${total.toFixed(2)}
          </span>
        </div>

        {/* Account credit — a tender under the total, never a discount on the price (credits D9) */}
        {hasCredit && (
          <>
            <div className="mt-2 flex justify-between items-center">
              <span className="text-fg-primary">Account credit</span>
              <span className="font-semibold text-green-600" data-testid="price-summary-credit">
                -${(credit.appliedCents / 100).toFixed(2)}
              </span>
            </div>
            {/* The card fee is charged on the card's share only, so the fee on the credit's
                share comes off — the receipt's "Processing Fee Adjustment" line, shown here so
                the rows add up to Due today. */}
            {credit.feeAdjustmentCents !== undefined && credit.feeAdjustmentCents < 0 && (
              <div className="mt-2 flex justify-between items-center">
                <span className="text-fg-primary">Processing Fee Adjustment</span>
                <span className="text-fg-primary" data-testid="price-summary-fee-adjustment">
                  -${(-credit.feeAdjustmentCents / 100).toFixed(2)}
                </span>
              </div>
            )}
            <div className="mt-2 flex justify-between items-center">
              <span className="font-semibold text-fg-primary">Due today</span>
              <span className="font-bold text-lg text-fg-primary" data-testid="price-summary-due-today">
                ${(credit.dueNowCents / 100).toFixed(2)}
              </span>
            </div>
          </>
        )}
      </div>
    </TicketCard>
  );
}
